import React, { useState, useEffect, useMemo } from "react";
import { db, storage } from "../../firebase";
import { doc, updateDoc, Timestamp, arrayUnion } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../AuthContext";

const GAME_LIBRARY = {
  diamond: ["Booty Chaser Bingo", "Treasure Hunt", "Lucky 7 Line-Up"],
  pilot: ["Piggy Bankin", "Ruby 8 Line"],
  none: [],
};

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function emptyBlock() {
  return { id: Date.now() + Math.random(), days: [], openTime: "", closeTime: "" };
}

// Used by charities (their linked bars), by bar owners (mode="owner"),
// who can manage gaming for their own bar whenever no charity account
// manages it, and by admins (mode="admin", adminBar = the bar being edited
// on Admin → Add a Bar), who can update any bar.
export default function Gaming({ mode = "charity", adminBar = null }) {
  const { user, userBars, refreshUserBars } = useAuth();

  const myBars = useMemo(
    () =>
      mode === "admin"
        ? adminBar
          ? [adminBar]
          : []
        : userBars.filter((bar) =>
            mode === "owner" ? bar.ownerId === user?.uid && !bar.charityId : bar.charityId === user?.uid
          ),
    [userBars, user, mode, adminBar]
  );


  const [selectedBarId, setSelectedBarId] = useState("");
  const [charityName, setCharityName] = useState("");
  const [software, setSoftware] = useState("diamond");
  const [terminals, setTerminals] = useState("");
  const [pulltabBoxes, setPulltabBoxes] = useState("");
  const [tipBoards, setTipBoards] = useState(false);
  // What kind of pull-tabs the bar has: machines (e-tabs), paper boxes, or both.
  const [hasEtabs, setHasEtabs] = useState(false);
  const [hasPaper, setHasPaper] = useState(false);
  // Machines that sell PAPER pull-tabs (not e-tabs).
  const [hasVending, setHasVending] = useState(false);
  const [vendingMachines, setVendingMachines] = useState("");
  const [hoursBlocks, setHoursBlocks] = useState([emptyBlock()]);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [existingPhotoURL, setExistingPhotoURL] = useState("");
  const [photoUpdatedAt, setPhotoUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Pick the bar automatically when there's only one.
  useEffect(() => {
    if (!selectedBarId && myBars.length === 1) setSelectedBarId(myBars[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBars]);

  useEffect(() => {
    if (!selectedBarId) return;
    const bar = myBars.find((b) => b.id === selectedBarId);
    const g = bar?.gaming;
    if (g) {
      setCharityName(g.charityName || "");
      setSoftware(g.software || "diamond");
      setTerminals(g.terminals ?? "");
      setPulltabBoxes(g.pulltabBoxes ?? "");
      setTipBoards(g.tipBoards || false);
      setHasEtabs(g.hasEtabs ?? Number(g.terminals) > 0);
      setHasPaper(g.hasPaperPulltabs ?? Number(g.pulltabBoxes) > 0);
      setHasVending(g.hasPulltabVending ?? Number(g.vendingMachines) > 0);
      setVendingMachines(g.vendingMachines ?? "");
      setHoursBlocks(g.hours && g.hours.length > 0 ? g.hours : [emptyBlock()]);
      setLicenseNumber(g.licenseNumber || "");
      setExistingPhotoURL(g.pulltabPhotoURL || "");
      setPhotoUpdatedAt(g.pulltabPhotoUpdatedAt || null);
    } else {
      setCharityName("");
      setSoftware("diamond");
      setTerminals("");
      setPulltabBoxes("");
      setTipBoards(false);
      setHasEtabs(false);
      setHasPaper(false);
      setHasVending(false);
      setVendingMachines("");
      setHoursBlocks([emptyBlock()]);
      setLicenseNumber("");
      setExistingPhotoURL("");
      setPhotoUpdatedAt(null);
    }
    setPhotoFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarId]);

  const toggleDayInBlock = (blockId, day) => {
    setHoursBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const has = b.days.includes(day);
        return { ...b, days: has ? b.days.filter((d) => d !== day) : [...b.days, day] };
      })
    );
  };

  const updateBlockTime = (blockId, field, value) => {
    setHoursBlocks((prev) => prev.map((b) => (b.id === blockId ? { ...b, [field]: value } : b)));
  };

  const addBlock = () => setHoursBlocks((prev) => [...prev, emptyBlock()]);

  const removeBlock = (blockId) => setHoursBlocks((prev) => prev.filter((b) => b.id !== blockId));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedBarId) return;
    setSaving(true);

    let photoURL = existingPhotoURL;
    let photoTimestamp = photoUpdatedAt;

    if (photoFile) {
      const fileRef = ref(storage, `gaming-photos/${selectedBarId}/${Date.now()}-${photoFile.name}`);
      await uploadBytes(fileRef, photoFile);
      photoURL = await getDownloadURL(fileRef);
      photoTimestamp = Timestamp.now();
    }

    const cleanedHours = hoursBlocks
      .filter((b) => b.days.length > 0 && b.openTime && b.closeTime)
      .map((b) => ({ days: b.days, openTime: b.openTime, closeTime: b.closeTime }));

    const barRef = doc(db, "bars", selectedBarId);
    const current = myBars.find((b) => b.id === selectedBarId)?.gaming || {};
    const tags = [];
    if (hasPaper || hasVending) tags.push("Pull-tabs");
    if (hasEtabs) tags.push("E-tabs");
    await updateDoc(barRef, {
      ...(tags.length ? { amenities: arrayUnion(...tags) } : {}),
      charitableGaming: true,
      gaming: {
        // keep anything already on file (like the state license site #)
        ...current,
        updatedBy: mode,
        updatedAt: Timestamp.now(),
        charityName,
        software: hasEtabs ? (software === "none" ? "diamond" : software) : "none",
        games: hasEtabs ? GAME_LIBRARY[software === "none" ? "diamond" : software] : [],
        hasEtabs,
        hasPaperPulltabs: hasPaper,
        hasPulltabVending: hasVending,
        vendingMachines: hasVending && vendingMachines ? Number(vendingMachines) : 0,
        terminals: hasEtabs && terminals ? Number(terminals) : 0,
        pulltabBoxes: hasPaper && pulltabBoxes ? Number(pulltabBoxes) : 0,
        tipBoards,
        hours: cleanedHours,
        licenseNumber,
        pulltabPhotoURL: photoURL,
        pulltabPhotoUpdatedAt: photoTimestamp,
      },
    });

    await refreshUserBars();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };

  if (myBars.length === 0) {
    return (
      <p style={{ color: "#fff" }}>
        You need to claim a bar first before you can set up gaming.
      </p>
    );
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "10px" }}>Gaming Inventory</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        {mode === "admin"
          ? "Update this bar's pull-tab boxes, e-tab machines, gaming hours and license. Saving here changes what customers see right away, even if a charity or owner manages this bar."
          : mode === "owner"
          ? "Keep your pull-tab boxes, e-tab machines and gaming hours up to date. Customers see this on your bar's page. If your charity creates its own account later, they'll take this over."
          : "Set up e-tabs, pull-tabs, hours, and licensing for each bar you operate gaming at."}
      </p>

      <form
        onSubmit={handleSave}
        style={{
          background: "#0D0B1A",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "560px",
          marginBottom: "30px",
        }}
      >
        <label style={labelStyle}>Venue</label>
        <select
          value={selectedBarId}
          onChange={(e) => setSelectedBarId(e.target.value)}
          required
          style={{ ...inputStyle, marginBottom: "18px" }}
        >
          <option value="">Select a bar</option>
          {myBars.map((bar) => (
            <option key={bar.id} value={bar.id}>{bar.name || "Unnamed Bar"}</option>
          ))}
        </select>

        <label style={labelStyle}>Charity / Organization Name</label>
        <input
          type="text"
          value={charityName}
          onChange={(e) => setCharityName(e.target.value)}
          placeholder="Lucky Bucket Bingo Association"
          style={{ ...inputStyle, marginBottom: "18px" }}
        />

        <label style={labelStyle}>What kind of pull-tabs?</label>
        <p style={{ fontSize: "15px", color: "#B8B0C8", margin: "0 0 10px" }}>
          Check everything this bar has. Counts are optional.
        </p>

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", cursor: "pointer" }}>
          <input type="checkbox" checked={hasEtabs} onChange={(e) => setHasEtabs(e.target.checked)} style={{ width: "20px", height: "20px" }} />
          <span>E-tabs (electronic pull-tabs played on a tablet or screen)</span>
        </label>
        {hasEtabs && (
          <div style={{ margin: "0 0 18px 30px" }}>
            <label style={{ ...labelStyle, fontSize: "15px" }}>How many e-tab tablets?</label>
            <input type="number" min="0" value={terminals} onChange={(e) => setTerminals(e.target.value)} style={{ ...inputStyle, marginBottom: "12px" }} />
            <label style={{ ...labelStyle, fontSize: "15px" }}>Which e-tab company?</label>
            <select value={software === "none" ? "diamond" : software} onChange={(e) => setSoftware(e.target.value)} style={inputStyle}>
              <option value="diamond">#1 Diamond Gaming</option>
              <option value="pilot">Pilot Games</option>
            </select>
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", cursor: "pointer" }}>
          <input type="checkbox" checked={hasPaper} onChange={(e) => setHasPaper(e.target.checked)} style={{ width: "20px", height: "20px" }} />
          <span>Paper pull-tabs sold at the bar or booth</span>
        </label>
        {hasPaper && (
          <div style={{ margin: "0 0 18px 30px" }}>
            <label style={{ ...labelStyle, fontSize: "15px" }}>How many boxes are open?</label>
            <input type="number" min="0" value={pulltabBoxes} onChange={(e) => setPulltabBoxes(e.target.value)} style={inputStyle} />
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", cursor: "pointer" }}>
          <input type="checkbox" checked={hasVending} onChange={(e) => setHasVending(e.target.checked)} style={{ width: "20px", height: "20px" }} />
          <span>Paper pull-tab machines (you buy paper tickets from a machine)</span>
        </label>
        {hasVending && (
          <div style={{ margin: "0 0 18px 30px" }}>
            <label style={{ ...labelStyle, fontSize: "15px" }}>How many machines?</label>
            <input type="number" min="0" value={vendingMachines} onChange={(e) => setVendingMachines(e.target.value)} style={inputStyle} />
          </div>
        )}
        <div style={{ height: "8px" }} />

        <label style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "22px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tipBoards}
            onChange={(e) => setTipBoards(e.target.checked)}
            style={{ width: "18px", height: "18px" }}
          />
          <span>This venue offers sports tip boards</span>
        </label>

        <label style={labelStyle}>Hours</label>
        <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 10px" }}>
          Add a separate block for each set of days with different hours — for example,
          Mon &amp; Tue 12:00 to 12:00, then Wed through Sun 11:00 to 12:00.
        </p>

        {hoursBlocks.map((block) => (
          <div
            key={block.id}
            style={{
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              padding: "14px",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", gap: "6px", marginBottom: "12px", flexWrap: "wrap" }}>
              {DAYS_SHORT.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDayInBlock(block.id, day)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: block.days.includes(day) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                    background: block.days.includes(day) ? "rgba(168,85,247,0.2)" : "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {day}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ ...labelStyle, fontSize: "16px" }}>Open</label>
                <input
                  type="time"
                  value={block.openTime}
                  onChange={(e) => updateBlockTime(block.id, "openTime", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ ...labelStyle, fontSize: "16px" }}>Close</label>
                <input
                  type="time"
                  value={block.closeTime}
                  onChange={(e) => updateBlockTime(block.id, "closeTime", e.target.value)}
                  style={inputStyle}
                />
              </div>
              {hoursBlocks.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBlock(block.id)}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    color: "#F472B6",
                    padding: "12px 14px",
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addBlock}
          style={{
            background: "transparent",
            border: "1px dashed rgba(255,255,255,0.3)",
            borderRadius: "8px",
            color: "#fff",
            padding: "10px",
            width: "100%",
            cursor: "pointer",
            marginBottom: "18px",
          }}
        >
          + Add another hours block
        </button>

        <label style={labelStyle}>License Number</label>
        <input
          type="text"
          value={licenseNumber}
          onChange={(e) => setLicenseNumber(e.target.value)}
          style={{ ...inputStyle, marginBottom: "18px" }}
        />

        <label style={labelStyle}>Pull-tab Booth Photo</label>
        <p style={{ fontSize: "16px", color: "#B8B0C8", margin: "0 0 8px" }}>
          Optional — customers will see this photo. Upload a new one any time, even daily.
        </p>
        {existingPhotoURL && !photoFile && (
          <img
            src={existingPhotoURL}
            alt="Current booth"
            style={{ width: "100%", maxWidth: "300px", borderRadius: "8px", marginBottom: "10px", display: "block" }}
          />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setPhotoFile(e.target.files[0] || null)}
          style={{ ...inputStyle, padding: "10px", marginBottom: "8px" }}
        />
        {photoUpdatedAt && (
          <p style={{ fontSize: "15px", color: "#B8B0C8", margin: "0 0 18px" }}>
            Last updated: {photoUpdatedAt.toDate ? photoUpdatedAt.toDate().toLocaleString() : ""}
          </p>
        )}

        <button
          type="submit"
          disabled={saving || !selectedBarId}
          style={{
            background: saved ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
            border: "none",
            padding: "14px",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: 600,
            fontSize: "15px",
            cursor: "pointer",
            width: "100%",
            marginTop: "8px",
          }}
        >
          {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Gaming Setup"}
        </button>
      </form>
    </div>
  );
}